import { skipToken, useQuery } from '@tanstack/react-query';
import { orpc } from '@/api/rpc/client';
import type {
  APInvoice,
  APInvoiceExtractionResult,
  GetAPInvoiceExtractionResultResponse,
  InvoiceChangeLogEntry,
  ListAPInvoiceChangeLogResponse,
  ListAPInvoiceSupportingDocumentsResponse,
  ListAPInvoicesInput,
  ListAPInvoicesResponse,
  GetAPInvoiceResponse,
  SupportingDocItem,
} from '@erp/rpc';

export function useAPInvoicesList(filters: ListAPInvoicesInput = {}) {
  const query = useQuery(
    orpc.accountsPayable.invoices.list.queryOptions({
      input: {
        ...filters,
        includeNumLineItemsMatched: true,
      },
    }),
  );

  const invoices = query.data?.invoices ?? [];
  const pagination = query.data?.pagination;

  return {
    ...query,
    invoices,
    pagination,
  } satisfies typeof query & {
    data?: ListAPInvoicesResponse;
    invoices: APInvoice[];
    pagination: ListAPInvoicesResponse['pagination'] | undefined;
  };
}

export function useAPInvoice(id: string | undefined, enabled = true) {
  const query = useQuery({
    ...orpc.accountsPayable.invoices.get.queryOptions({
      input: id ? { id } : skipToken,
    }),
    enabled: Boolean(id) && enabled,
  });

  return {
    ...query,
    invoice: query.data ?? null,
  } satisfies typeof query & {
    data?: GetAPInvoiceResponse;
    invoice: GetAPInvoiceResponse | null;
  };
}

export function useAPInvoiceChangeLog(
  apInvoiceId: string | undefined,
  enabled = true,
) {
  const query = useQuery({
    ...orpc.accountsPayable.invoices.listChangeLog.queryOptions({
      input: apInvoiceId ? { apInvoiceId } : skipToken,
    }),
    enabled: Boolean(apInvoiceId) && enabled,
  });

  return {
    ...query,
    entries: query.data?.entries ?? [],
  } satisfies typeof query & {
    data?: ListAPInvoiceChangeLogResponse;
    entries: InvoiceChangeLogEntry[];
  };
}

export function useAPInvoiceExtractionResult(
  id: string | undefined,
  enabled = true,
) {
  const query = useQuery({
    ...orpc.accountsPayable.invoices.getExtractionResult.queryOptions({
      input: id ? { id } : skipToken,
    }),
    enabled: Boolean(id) && enabled,
  });

  return {
    ...query,
    extractionResult: query.data?.extractionResult ?? null,
  } satisfies typeof query & {
    data?: GetAPInvoiceExtractionResultResponse;
    extractionResult: APInvoiceExtractionResult | null;
  };
}

export function useAPInvoiceSupportingDocuments(
  apInvoiceId: string | undefined,
  enabled = true,
) {
  const query = useQuery({
    ...orpc.accountsPayable.invoices.listSupportingDocuments.queryOptions({
      input: apInvoiceId ? { apInvoiceId } : skipToken,
    }),
    enabled: Boolean(apInvoiceId) && enabled,
  });

  return {
    ...query,
    documents: query.data?.documents ?? [],
  } satisfies typeof query & {
    data?: ListAPInvoiceSupportingDocumentsResponse;
    documents: SupportingDocItem[];
  };
}
